import { useEffect, useState } from 'react'
import { ToggleChip as Chip, DataTable, ErrorBox, Panel, ScreenHeading, Spinner } from '../../components/ui'
import { api, errorMessage } from '../../lib/api'
import { dateTime } from '../../lib/format'
import { useT } from '../../lib/i18n'

interface AuditRow {
  id: number
  at: string
  actor: string | null
  actorName: string | null
  action: string
  entityType: string
  entityId: string
  before: unknown
  after: unknown
}

const FILTERS = [
  { key: '', label: 'Everything' },
  { key: 'model_version.', label: 'Model versions' },
  { key: 'escalation.', label: 'Escalations' },
  { key: 'quote.', label: 'Quotes' },
  { key: 'import.', label: 'Imports' },
  { key: 'user.', label: 'Users' },
]

export function Audit() {
  const { t } = useT()
  const [filter, setFilter] = useState('')
  const [rows, setRows] = useState<AuditRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState<number | null>(null)
  useEffect(() => {
    setRows(null)
    api.get<AuditRow[]>(`/api/audit?limit=300${filter ? `&action=${encodeURIComponent(filter)}` : ''}`).then(setRows).catch((e) => setError(errorMessage(e)))
  }, [filter])
  const pretty = (v: unknown) => (v === null || v === undefined ? '–' : JSON.stringify(v, null, 1).replace(/\u0000/g, '␀'))
  return (
    <div>
      <ScreenHeading title={t('Audit log')} />
      <div className="mb-3 flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <Chip key={f.key} active={filter === f.key} onClick={() => setFilter(f.key)}>
            {t(f.label)}
          </Chip>
        ))}
      </div>
      {error && <ErrorBox>{error}</ErrorBox>}
      {!rows ? (
        <Spinner />
      ) : (
        <Panel>
          <DataTable
            rows={rows}
            rowKey={(r) => String(r.id)}
            onRowClick={(r) => setOpen(open === r.id ? null : r.id)}
            columns={[
              { key: 'at', header: t('When'), render: (r) => <span className="text-slate-400">{dateTime(r.at)}</span> },
              { key: 'who', header: t('Actor'), render: (r) => r.actorName ?? <span className="text-slate-600">{t('system')}</span> },
              { key: 'a', header: t('Action'), render: (r) => <span className="font-mono text-xs text-slate-200">{r.action}</span> },
              { key: 'e', header: t('Entity'), render: (r) => <span className="text-xs text-slate-400">{r.entityType} · {r.entityId.length > 12 ? r.entityId.slice(0, 8) : r.entityId}</span> },
              { key: 'ba', header: t('Before → after'), render: (r) => (
                open === r.id ? (
                  <div className="grid gap-2 md:grid-cols-2">
                    <pre className="max-h-64 overflow-auto rounded bg-slate-900/40 p-2 font-mono text-[11px] text-slate-400">{pretty(r.before)}</pre>
                    <pre className="max-h-64 overflow-auto rounded bg-slate-900/40 p-2 font-mono text-[11px] text-slate-300">{pretty(r.after)}</pre>
                  </div>
                ) : (
                  <span className="text-xs text-slate-500">{JSON.stringify(r.after ?? r.before ?? null)?.slice(0, 120)}</span>
                )
              ) },
            ]}
          />
        </Panel>
      )}
    </div>
  )
}
